import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import { habitAPI } from '../lib/supabase'
import HabitForm from './HabitForm' 

const { FiPlus, FiEdit2, FiTrash2, FiTarget, FiTrendingUp, FiFilter, FiSearch, FiZap, FiAward, FiActivity } = FiIcons 

const HabitList = () => {
  const [habits, setHabits] = useState([]) 
  const [loading, setLoading] = useState(true) 
  const [showForm, setShowForm] = useState(false) 
  const [editingHabit, setEditingHabit] = useState(null) 
  const [searchTerm, setSearchTerm] = useState('') 
  const [selectedCategory, setSelectedCategory] = useState('all') 
  const [sortBy, setSortBy] = useState('recent') 

  const categories = [ 
    { id: 'all', name: 'All' },
    { id: 'wellness', name: 'Wellness' },
    { id: 'fitness', name: 'Fitness' },
    { id: 'learning', name: 'Learning' },
    { id: 'productivity', name: 'Work' },
    { id: 'social', name: 'Social' },
    { id: 'creativity', name: 'Creative' } 
  ] 

  useEffect(() => {
    loadHabits()
  }, []) 

  const loadHabits = async () => {
    try {
      const { data, error } = await habitAPI.getHabits() 
      if (error) throw error 
      setHabits(data || [])
    } catch (error) {
      console.error('Error loading habits:', error) 
    } finally {
      setLoading(false)
    } 
  } 

  const handleCreateHabit = () => {
    console.log("Create habit button clicked")
    setEditingHabit(null) 
    setShowForm(true)
  } 

  const handleEditHabit = (habit) => {
    setEditingHabit(habit) 
    setShowForm(true)
  } 

  const handleDeleteHabit = async (habitId) => {
    if (!confirm('Delete this habit?')) return 
    try {
      await habitAPI.deleteHabit(habitId) 
      setHabits(prev => prev.filter(h => h.id !== habitId))
    } catch (error) {
      console.error('Error deleting habit:', error) 
    } 
  } 

  const handleSaveHabit = (savedHabit) => {
    if (editingHabit) {
      setHabits(prev => prev.map(h => h.id === savedHabit.id ? savedHabit : h))
    } else {
      setHabits(prev => [savedHabit, ...prev])
    } 
  } 

  const getCategoryColor = (category) => {
    const colors = {
      wellness: 'from-emerald-400 to-green-600',
      fitness: 'from-blue-400 to-indigo-600',
      learning: 'from-purple-400 to-violet-600',
      productivity: 'from-forge-400 to-orange-500',
      social: 'from-pink-400 to-rose-600',
      creativity: 'from-indigo-400 to-purple-600'
    } 
    return colors[category] || 'from-forge-400 to-forge-600'
  } 

  const getCategoryBadgeClasses = (category) => {
    const classes = {
      wellness: 'bg-emerald-500/20 text-emerald-400 border-emerald-400/30',
      fitness: 'bg-blue-500/20 text-blue-400 border-blue-400/30',
      learning: 'bg-purple-500/20 text-purple-400 border-purple-400/30',
      productivity: 'bg-forge-500/20 text-forge-400 border-forge-400/30',
      social: 'bg-pink-500/20 text-pink-400 border-pink-400/30',
      creativity: 'bg-indigo-500/20 text-indigo-400 border-indigo-400/30'
    } 
    return classes[category] || 'bg-forge-500/20 text-forge-400 border-forge-400/30'
  } 

  const filteredHabits = habits 
    .filter(habit => 
      habit.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
      habit.description.toLowerCase().includes(searchTerm.toLowerCase()) 
    ) 
    .filter(habit => selectedCategory === 'all' || habit.category === selectedCategory) 
    .sort((a, b) => {
      switch (sortBy) {
        case 'streak': return b.streak - a.streak 
        case 'alphabetical': return a.title.localeCompare(b.title) 
        case 'category': return a.category.localeCompare(b.category) 
        default: return new Date(b.created_at) - new Date(a.created_at)
      }
    }) 

  if (loading) {
    return ( 
      <div className="min-h-screen bg-dominance-gradient flex items-center justify-center"> 
        <motion.div 
          initial={{ opacity: 0, scale: 0.8 }} 
          animate={{ opacity: 1, scale: 1 }} 
          className="text-center" 
        > 
          <div className="w-16 h-16 border-4 border-forge-400/30 border-t-forge-400 rounded-full animate-spin mb-4"></div> 
          <h3 className="text-xl font-bold text-forge-400 mb-2">Loading Habits</h3> 
          <p className="text-forge-300">Preparing your habit collection...</p> 
        </motion.div> 
      </div> 
    ) 
  } 

  return ( 
    <div className="min-h-screen bg-dominance-gradient"> 
      <div className="max-w-6xl mx-auto px-4 py-6"> 
        {/* Header */} 
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          className="text-center mb-6" 
        > 
          <div className="flex items-center justify-center space-x-2 mb-4"> 
            <SafeIcon icon={FiTarget} className="text-2xl text-forge-400" /> 
            <h1 className="text-3xl font-bold bg-forge-gradient bg-clip-text text-transparent"> 
              My Habits 
            </h1> 
          </div> 
          <p className="text-forge-300 mb-6 px-4 text-sm sm:text-base"> 
            Build lasting habits that transform your life 
          </p> 
        </motion.div> 

        {/* Create Habit Button - Always Visible for all devices */}
        <div className="mb-6">
          <button 
            onClick={handleCreateHabit} 
            className="w-full sm:w-auto bg-forge-gradient text-shadow-950 px-6 py-4 rounded-xl font-semibold hover:shadow-power transition-all duration-300 flex items-center justify-center space-x-2 mx-auto"
          > 
            <SafeIcon icon={FiPlus} /> 
            <span>Create Habit</span> 
          </button>
        </div>

        {/* Controls */} 
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.1 }} 
          className="bg-gradient-to-r from-shadow-900/90 to-shadow-800/90 backdrop-blur-sm rounded-xl shadow-power border border-forge-400/30 p-4 mb-6" 
        > 
          <div className="space-y-4"> 
            {/* Search */} 
            <div className="relative"> 
              <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-forge-400" /> 
              <input 
                type="text" 
                placeholder="Search habits..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                className="w-full pl-10 pr-4 py-3 bg-shadow-800/50 border border-forge-400/30 rounded-xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 text-forge-200 placeholder-forge-300/50" 
              /> 
            </div> 
            
            {/* Filters - Mobile-friendly layout */} 
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4"> 
              <select 
                value={selectedCategory} 
                onChange={(e) => setSelectedCategory(e.target.value)} 
                className="w-full px-4 py-3 bg-shadow-800/50 border border-forge-400/30 rounded-xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 text-forge-200" 
              > 
                {categories.map(category => ( 
                  <option key={category.id} value={category.id}> 
                    {category.name} 
                  </option> 
                ))} 
              </select>
              
              <select 
                value={sortBy} 
                onChange={(e) => setSortBy(e.target.value)} 
                className="w-full px-4 py-3 bg-shadow-800/50 border border-forge-400/30 rounded-xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 text-forge-200" 
              > 
                <option value="recent">Most Recent</option> 
                <option value="streak">Longest Streak</option> 
                <option value="alphabetical">Alphabetical</option> 
                <option value="category">Category</option> 
              </select> 
            </div> 
          </div> 
        </motion.div> 

        {/* Habits List */} 
        {filteredHabits.length === 0 ? ( 
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }} 
            animate={{ opacity: 1, scale: 1 }} 
            className="text-center py-12" 
          > 
            <div className="w-20 h-20 bg-forge-gradient rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-power"> 
              <SafeIcon icon={FiTarget} className="text-shadow-950 text-2xl" /> 
            </div> 
            <h3 className="text-xl font-bold text-forge-400 mb-4"> 
              {habits.length === 0 ? 'No habits yet' : 'No habits match your filters'} 
            </h3> 
            <p className="text-forge-300 mb-8 px-4"> 
              {habits.length === 0 
                ? 'Create your first habit to start building better routines!' 
                : 'Try adjusting your search or filter criteria.'
              } 
            </p> 
            {habits.length === 0 && ( 
              <button 
                onClick={handleCreateHabit} 
                className="bg-forge-gradient text-shadow-950 px-8 py-4 rounded-xl font-semibold hover:shadow-power transition-all duration-300" 
              > 
                Create Your First Habit
              </button> 
            )} 
          </motion.div> 
        ) : ( 
          <div className="space-y-4 pb-20 sm:pb-4"> 
            <AnimatePresence> 
              {filteredHabits.map((habit, index) => ( 
                <motion.div 
                  key={habit.id} 
                  initial={{ opacity: 0, y: 20 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  exit={{ opacity: 0, y: -20 }} 
                  transition={{ delay: index * 0.05 }} 
                  className="bg-gradient-to-r from-shadow-900/90 to-shadow-800/90 backdrop-blur-sm rounded-xl shadow-power border border-forge-400/30 p-4 sm:p-6 hover:shadow-dominance transition-all duration-300" 
                > 
                  <div className="flex items-start gap-3 sm:gap-4"> 
                    {/* Habit Icon */} 
                    <div className="relative flex-shrink-0"> 
                      <div className={`w-12 h-12 bg-gradient-to-r ${getCategoryColor(habit.category)} rounded-xl flex items-center justify-center text-white text-xl shadow-lg`}> 
                        {habit.icon} 
                      </div> 
                      {habit.streak > 0 && ( 
                        <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-r from-forge-400 to-orange-500 rounded-full flex items-center justify-center"> 
                          <span className="text-shadow-950 text-xs font-bold">{habit.streak}</span> 
                        </div> 
                      )} 
                    </div> 

                    {/* Habit Info */} 
                    <div className="flex-1 min-w-0"> 
                      <div className="flex items-start justify-between mb-2"> 
                        <h3 className="text-base sm:text-lg font-bold text-forge-400 truncate pr-2">{habit.title}</h3> 
                        <div className="flex space-x-1 flex-shrink-0"> 
                          <button 
                            onClick={() => handleEditHabit(habit)} 
                            className="p-2 text-forge-400 hover:text-forge-200 hover:bg-forge-400/20 rounded-lg transition-all duration-200" 
                          > 
                            <SafeIcon icon={FiEdit2} className="text-sm" /> 
                          </button> 
                          <button 
                            onClick={() => handleDeleteHabit(habit.id)} 
                            className="p-2 text-forge-400 hover:text-danger-400 hover:bg-danger-400/20 rounded-lg transition-all duration-200" 
                          > 
                            <SafeIcon icon={FiTrash2} className="text-sm" /> 
                          </button> 
                        </div> 
                      </div> 
                      <p className="text-forge-300 text-sm mb-4 line-clamp-2">{habit.description}</p> 

                      {/* Stats */} 
                      <div className="flex flex-wrap items-center gap-2 mb-4"> 
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getCategoryBadgeClasses(habit.category)}`}> 
                          {habit.category.charAt(0).toUpperCase() + habit.category.slice(1)} 
                        </span> 
                        <div className="flex items-center space-x-1 text-emerald-400"> 
                          <SafeIcon icon={FiTrendingUp} className="text-xs" /> 
                          <span className="text-xs font-medium">{habit.streak} day streak</span> 
                        </div> 
                        <div className="flex items-center space-x-1 text-blue-400"> 
                          <SafeIcon icon={FiTarget} className="text-xs" /> 
                          <span className="text-xs font-medium">{habit.target_days} day goal</span> 
                        </div> 
                        {habit.goal_amount && habit.goal_unit && ( 
                          <div className="flex items-center space-x-1 text-purple-400"> 
                            <SafeIcon icon={FiActivity} className="text-xs" /> 
                            <span className="text-xs font-medium">{habit.goal_amount} {habit.goal_unit} daily</span> 
                          </div> 
                        )} 
                      </div> 

                      {/* Progress Bar */} 
                      <div className="mb-4"> 
                        <div className="flex items-center justify-between mb-2"> 
                          <span className="text-xs font-medium text-forge-300">Progress to Goal</span> 
                          <span className="text-xs font-bold text-forge-400"> 
                            {Math.round((habit.streak / habit.target_days) * 100)}% 
                          </span> 
                        </div> 
                        <div className="w-full bg-shadow-800/50 rounded-full h-2"> 
                          <motion.div 
                            initial={{ width: 0 }} 
                            animate={{ width: `${Math.min((habit.streak / habit.target_days) * 100, 100)}%` }} 
                            transition={{ duration: 1, ease: "easeOut", delay: index * 0.1 }} 
                            className={`bg-gradient-to-r ${getCategoryColor(habit.category)} h-2 rounded-full`} 
                          /> 
                        </div> 
                      </div> 

                      {/* Achievement Badges */} 
                      <div className="flex flex-wrap gap-2"> 
                        {habit.streak >= 30 && ( 
                          <div className="flex items-center space-x-1 bg-gradient-to-r from-forge-400 to-orange-500 text-shadow-950 px-2 py-1 rounded-full text-xs font-medium"> 
                            <SafeIcon icon={FiAward} className="text-xs" /> 
                            <span>30 Day Champion</span> 
                          </div> 
                        )} 
                        {habit.streak >= 7 && ( 
                          <div className="flex items-center space-x-1 bg-gradient-to-r from-emerald-400 to-green-500 text-white px-2 py-1 rounded-full text-xs font-medium"> 
                            <SafeIcon icon={FiZap} className="text-xs" /> 
                            <span>Week Strong</span> 
                          </div> 
                        )} 
                      </div> 
                    </div> 
                  </div> 
                </motion.div> 
              ))} 
            </AnimatePresence> 
          </div> 
        )} 

        {/* Floating Create Habit Button for Mobile */}
        <div className="fixed bottom-6 right-6 z-50 sm:hidden">
          <button
            onClick={handleCreateHabit}
            className="w-16 h-16 bg-forge-gradient text-shadow-950 rounded-full shadow-forge flex items-center justify-center"
            style={{touchAction: 'manipulation'}}
          >
            <SafeIcon icon={FiPlus} className="text-2xl" />
          </button>
        </div>

        {/* Habit Form Modal */}
        <HabitForm 
          isOpen={showForm} 
          onClose={() => setShowForm(false)} 
          onSave={handleSaveHabit} 
          habit={editingHabit} 
        /> 
      </div> 
    </div> 
  )
}

export default HabitList